import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiHelper } from '../../utils/apiHelper';

const initialState = {
  statistics: {
    totalUsers: 0,
    totalStartups: 0,
    totalInvestments: 0,
    activeStartups: 0,
  },
  services: {
    mkk: { status: false, duration: '0ms' },
    backend: { status: false, duration: '0ms' },
    mkkSyncer: { status: false, duration: '0ms' }
  },
  users: {
    list: [],
    count: 0,
  },
  pendingCorporateUsers: {
    list: [],
    loading: false,
    error: null
  },
  selectedUser: null,
  userStartups: [],
  selectedStartup: null,
  userActivities: {
    list: [],
    count: 0,
  },
  userInvestments: {
    list: [],
    count: 0,
    loading: false,
    error: null
  },
  startupInvestments: {
    list: [],
    count: 0,
    loading: false,
    error: null
  },
  pendingStartups: {
    list: [],
    count: 0,
  },
  committees: [],
  pendingCommitteeStartups: {
    list: [],
    count: 0,
  },
  loading: false,
  error: null,
  allStartups: {
    list: [],
    count: 0,
  },
  allInvestments: {
    list: [],
    count: 0,
    loading: false,
    error: null
  },
  preFundInvestments: {
    list: [],
    count: 0,
    loading: false,
    error: null
  },
  entrepreneurForm: null,
  waitingComments: [],
  loadingComments: false,
  errorComments: null
};

export const fetchAllInvestments = createAsyncThunk(
  'admin/fetchAllInvestments',
  async (params) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      queryParams.append('page', params.page || 1);
      queryParams.append('limit', params.limit || 10);
      
      // Add filter params if they exist
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.amountRange) queryParams.append('amountRange', params.amountRange);
      if (params.paymentType) queryParams.append('paymentType', params.paymentType);
      if (params.status) queryParams.append('status', params.status);
      if (params.startupId) queryParams.append('startupId', params.startupId);
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.investmentId) queryParams.append('investmentId', params.investmentId);

      const response = await ApiHelper.get(`/admin/all-investments?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.code === "00000") {
        return response.data.investments;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPreFundInvestments = createAsyncThunk(
  'admin/fetchPreFundInvestments',
  async (params) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      queryParams.append('page', params.page || 1);
      queryParams.append('limit', params.limit || 10);
      
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.startupId) queryParams.append('startupId', params.startupId);
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.investmentId) queryParams.append('investmentId', params.investmentId);

      const response = await ApiHelper.get(`/admin/pre-fund-list?${queryParams.toString()}`);
      
      if (response.data.code === "00000") {
        return response.data.investments;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const fetchUserInvestments = createAsyncThunk(
  'admin/fetchUserInvestments',
  async ({ userId, page, limit }) => {
    try {
      const response = await ApiHelper.get(`/admin/investment-by-user-id/${userId}?page=${page}&limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.code === "00000") {
        return response.data.investments;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const fetchStartupInvestments = createAsyncThunk(
  'admin/fetchStartupInvestments',
  async ({ startupId, page, limit }) => {
    try {
      const response = await ApiHelper.get(`/admin/investment-by-startup-id/${startupId}?page=${page}&limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.code === "00000") {
        return response.data.investments;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const fetchPendingCorporateUsers = createAsyncThunk(
  'admin/fetchPendingCorporateUsers',
  async () => {
    try {
      const response = await ApiHelper.get('/admin/waiting-approval-corporate-users');
      if (response.data.code === "00000") {
        return response.data.corporateUsers;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const approveCorporateUser = createAsyncThunk(
  'admin/approveCorporateUser',
  async (userId) => {
    try {
      const response = await ApiHelper.post('/admin/approve-corporate-user', { userId });
      if (response.data.code === "00000") {
        return userId;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const updateStartupDates = createAsyncThunk(
  'admin/updateStartupDates',
  async ({ startupId, startDate, endDate }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);
      formData.append('startDate', startDate);
      formData.append('endDate', endDate);

      const response = await ApiHelper.put('/admin/update-startup-dates', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.code === "00000") {
        return { startupId, startDate, endDate };
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const updateStartupShortlinkPath = createAsyncThunk(
  'admin/updateStartupShortlinkPath',
  async ({ startupId, shortlinkPath }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);
      formData.append('shortlinkPath', shortlinkPath ?? '');

      const response = await ApiHelper.post('/admin/update-startup-shortlink', formData.toString());

      if (response.data.code === "00000") {
        return { startupId, shortlinkPath: shortlinkPath ?? '' };
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }
);

export const activePreFunding = createAsyncThunk(
  'admin/activePreFunding', 
  async ({ startupId, endDate }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);
      formData.append('endDate', endDate);

      const response = await ApiHelper.post('/admin/active-pre-fund-investment', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.code === "00000") {
        return { startupId, endDate };
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
)

export const deactivePreFunding = createAsyncThunk(
  'admin/deactivePreFunding',
  async ({ startupId }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);

      const response = await ApiHelper.post('/admin/deactive-pre-fund-investment', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.code === "00000") {
        return { startupId };
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
)

export const shareStartup = createAsyncThunk(
  'admin/shareStartup',
  async (startupId) => {
    try {
      const response = await ApiHelper.post(`/admin/share-startup/${startupId}`);
      if (response.data.code === "00000") {
        return startupId;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const fetchEntrepreneurForm = createAsyncThunk(
  'admin/fetchEntrepreneurForm',
  async (userId) => {
    try {
      const response = await ApiHelper.get(`/admin/user-entrepreneur-info/${userId}`);
      if (response.data.code === "00000") {
        return response.data.entrepreneurForm;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);

export const updateEntrepreneurForm = createAsyncThunk(
  'admin/updateEntrepreneurForm',
  async ({ userId, formData, cvPath }) => {
    try {
      const params = new URLSearchParams();
      params.append('userId', userId);
      
      // Append all form data fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          params.append(key, formData[key]);
        }
      });

      if (cvPath) {
        params.append('cvFilePath', cvPath);
      }

      const response = await ApiHelper.post('/admin/update-user-entrepreneur-info', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data.code === "00000") {
        return formData;
      }
      throw new Error(response.data.message);
    } catch (error) {
      throw error;
    }
  }
);


export const updateUserPassword = createAsyncThunk(
  'admin/updateUserPassword',
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      const response = await ApiHelper.post(`/admin/update-user-password`, {
        userId,
        password
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Şifre güncellenirken bir hata oluştu');
    }
  }
);

export const fetchWaitingComments = createAsyncThunk(
  'admin/fetchWaitingComments',
  async (_, { rejectWithValue }) => {
      try {
          const response = await ApiHelper.get('/admin/waiting-comments');
          return response.data.responseArray;
      } catch (error) {
          return rejectWithValue(error.response?.data || 'Bir hata oluştu');
      }
  }
);

export const fetchWaitingUpdates = createAsyncThunk(
  'admin/fetchWaitingUpdates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiHelper.get('/admin/waiting-updates');
      return response.data.responseArray;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Bir hata oluştu');
    }
  }
);

export const approveQuestion = createAsyncThunk(
  'admin/approveQuestion',
  async ({ questionId, startupId }, { rejectWithValue }) => {
      try {
          await ApiHelper.post('/admin/approve-question', {
              questionId,
              startupId
          });
          return { questionId, startupId };
      } catch (error) {
          return rejectWithValue(error.response?.data || 'Bir hata oluştu');
      }
  }
);

export const approveAnswer = createAsyncThunk(
  'admin/approveAnswer',
  async ({ answerId, startupId }, { rejectWithValue }) => {
      try {
          await ApiHelper.post('/admin/approve-answer', {
              answerId,
              startupId
          });
          return { answerId, startupId };
      } catch (error) {
          return rejectWithValue(error.response?.data || 'Bir hata oluştu');
      }
  }
);

export const approveUpdate = createAsyncThunk(
  'admin/approveUpdate',
  async ({ updateId, startupId }, { rejectWithValue }) => {
    try {
      await ApiHelper.post('/admin/approve-update', {
        updateId,
        startupId
      });
      return { updateId, startupId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Bir hata oluştu');
    }
  }
);

export const deleteQuestion = createAsyncThunk(
  'admin/deleteQuestion',
  async ({ questionId, startupId }, { rejectWithValue }) => {
    try {
      await ApiHelper.delete(`/admin/delete-question/${questionId}`);
      return { questionId, startupId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Bir hata oluştu');
    }
  }
);

export const deleteAnswer = createAsyncThunk(
  'admin/deleteAnswer',
  async ({ answerId, startupId }, { rejectWithValue }) => {
    try {
      await ApiHelper.delete(`/admin/delete-answer/${answerId}`);
      return { answerId, startupId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Bir hata oluştu');
    }
  }
);

export const activeShowing = createAsyncThunk(
  'admin/active-card',
  async ({ startupId }, { rejectWithValue }) => {
      try {
          const response = await ApiHelper.post('/admin/active-card', {
              startupId
          });
          return response.data;
      } catch (error) {
          return rejectWithValue(error.response?.message || 'Bir hata oluştu');
      }
  }
)

export const deactiveShowing = createAsyncThunk(
  'admin/deactive-card',
  async ({ startupId }, { rejectWithValue }) => {
      try {
          const response = await ApiHelper.post('/admin/deactive-card', {
              startupId
          });
          return response.data;
      } catch (error) {
          return rejectWithValue(error.response?.message || 'Bir hata oluştu');
      }
  }
)

export const activeDetail = createAsyncThunk(
  'admin/active-detail',
  async ({ startupId }, { rejectWithValue }) => {
      try {
          const response = await ApiHelper.post('/admin/active-detail', {
              startupId
          });
          return response.data;
      } catch (error) {
          return rejectWithValue(error.response?.message || 'Bir hata oluştu');
      }
  }
)

export const deactiveDetail = createAsyncThunk(
  'admin/deactive-detail',
  async ({ startupId }, { rejectWithValue }) => {
      try {
          const response = await ApiHelper.post('/admin/deactive-detail', {
              startupId
          });
          return response.data;
      } catch (error) {
          return rejectWithValue(error.response?.message || 'Bir hata oluştu');
      }
  }
)

export const loginAsUser = createAsyncThunk(
  'admin/loginAsUser',
  async (userId, { rejectWithValue }) => {
    try { 
      const response = await ApiHelper.post('/admin/login-as-user', { userId });
      if (response.data.code === "00000") {
        return response.data;
      }
      throw new Error(response.data.message);
  } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Kullanıcı olarak giriş yapılırken bir hata oluştu');
  }
}
) 

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    fetchStatisticsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchStatisticsSuccess: (state, action) => {
      state.loading = false;
      state.statistics = action.payload;
    },
    fetchStatisticsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchServicesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchServicesSuccess: (state, action) => {
      state.loading = false;
      state.services = action.payload;
    },
    fetchServicesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUsersStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUsersSuccess: (state, action) => {
      state.loading = false;
      state.users = {
        list: action.payload.userList,
        count: action.payload.count
      };
    },
    fetchUsersFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUserDetailStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUserDetailSuccess: (state, action) => {
      state.loading = false;
      state.selectedUser = action.payload;
    },
    fetchUserDetailFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    blockUserStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    blockUserSuccess: (state) => {
      state.loading = false;
      if (state.selectedUser) {
        state.selectedUser.enabled = false;
      }
    },
    blockUserFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    unblockUserStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    unblockUserSuccess: (state) => {
      state.loading = false;
      if (state.selectedUser) {
        state.selectedUser.enabled = true;
      }
    },
    unblockUserFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUserStartupsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUserStartupsSuccess: (state, action) => {
      state.loading = false;
      state.userStartups = action.payload;
    },
    fetchUserStartupsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchStartupDetailStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchStartupDetailSuccess: (state, action) => {
      state.loading = false;
      state.selectedStartup = action.payload;
    },
    fetchStartupDetailFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchUserActivitiesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUserActivitiesSuccess: (state, action) => {
      state.loading = false;
      state.userActivities = {
        list: action.payload.rows,
        count: action.payload.count,
      };
    },
    fetchUserActivitiesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchPendingStartupsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPendingStartupsSuccess: (state, action) => {
      state.loading = false;
      state.pendingStartups = {
        list: action.payload.list || [],
        count: action.payload.count || 0,
      };
    },
    fetchPendingStartupsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeStartupStatusStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    changeStartupStatusSuccess: (state, action) => {
      state.loading = false;
      if (state.selectedStartup) {
        state.selectedStartup.status = action.payload;
      }
    },
    changeStartupStatusFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchCommitteesStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchCommitteesSuccess: (state, action) => {
      state.loading = false;
      state.committees = action.payload;
    },
    fetchCommitteesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchPendingCommitteeStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPendingCommitteeSuccess: (state, action) => {
      state.loading = false;
      state.pendingCommitteeStartups = action.payload;
    },
    fetchPendingCommitteeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchAllStartupsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAllStartupsSuccess: (state, action) => {
      state.loading = false;
      state.allStartups = {
        list: action.payload.list || [],
        count: action.payload.count || 0
      };
    },
    fetchAllStartupsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllInvestments.pending, (state) => {
        state.allInvestments.loading = true;
        state.allInvestments.error = null;
      })
      .addCase(fetchAllInvestments.fulfilled, (state, action) => {
        state.allInvestments.loading = false;
        state.allInvestments.list = action.payload.rows;
        state.allInvestments.count = action.payload.count;
      })
      .addCase(fetchAllInvestments.rejected, (state, action) => {
        state.allInvestments.loading = false;
        state.allInvestments.error = action.error.message;
      })
      .addCase(fetchPreFundInvestments.pending, (state) => {
        state.preFundInvestments.loading = true;
        state.preFundInvestments.error = null;
      })
      .addCase(fetchPreFundInvestments.fulfilled, (state, action) => {
        state.preFundInvestments.loading = false;
        state.preFundInvestments.list = action.payload.rows;
        state.preFundInvestments.count = action.payload.count;
      })
      .addCase(fetchPreFundInvestments.rejected, (state, action) => {
        state.preFundInvestments.loading = false;
        state.preFundInvestments.error = action.error.message;
      })
      .addCase(fetchUserInvestments.pending, (state) => {
        state.userInvestments.loading = true;
        state.userInvestments.error = null;
      })
      .addCase(fetchUserInvestments.fulfilled, (state, action) => {
        state.userInvestments.loading = false;
        state.userInvestments.list = action.payload.rows || [];
        state.userInvestments.count = action.payload.count || 0;
      })
      .addCase(fetchUserInvestments.rejected, (state, action) => {
        state.userInvestments.loading = false;
        state.userInvestments.error = action.error.message;
      })
      .addCase(fetchStartupInvestments.pending, (state) => {
        state.startupInvestments.loading = true;
        state.startupInvestments.error = null;
      })
      .addCase(fetchStartupInvestments.fulfilled, (state, action) => {
        state.startupInvestments.loading = false;
        state.startupInvestments.list = action.payload.rows || [];
        state.startupInvestments.count = action.payload.count || 0;
      })
      .addCase(fetchStartupInvestments.rejected, (state, action) => {
        state.startupInvestments.loading = false;
        state.startupInvestments.error = action.error.message;
      })
      .addCase(fetchPendingCorporateUsers.pending, (state) => {
        state.pendingCorporateUsers.loading = true;
        state.pendingCorporateUsers.error = null;
      })
      .addCase(fetchPendingCorporateUsers.fulfilled, (state, action) => {
        state.pendingCorporateUsers.loading = false;
        state.pendingCorporateUsers.list = action.payload;
      })
      .addCase(fetchPendingCorporateUsers.rejected, (state, action) => {
        state.pendingCorporateUsers.loading = false;
        state.pendingCorporateUsers.error = action.error.message;
      })
      .addCase(approveCorporateUser.fulfilled, (state, action) => {
        state.pendingCorporateUsers.list = state.pendingCorporateUsers.list.filter(
          user => user.userId !== action.payload
        );
      })
      .addCase(updateStartupDates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStartupDates.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup) {
          state.selectedStartup.startDate = action.payload.startDate;
          state.selectedStartup.endDate = action.payload.endDate;
        }
      })
      .addCase(updateStartupDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateStartupShortlinkPath.fulfilled, (state, action) => {
        if (state.selectedStartup && state.selectedStartup.startupId === action.payload.startupId) {
          state.selectedStartup.shortlinkPath = action.payload.shortlinkPath;
        }
      })
      .addCase(activePreFunding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activePreFunding.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(activePreFunding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deactivePreFunding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivePreFunding.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deactivePreFunding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(shareStartup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(shareStartup.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup && state.selectedStartup.startupId === action.payload) {
          state.selectedStartup.status = 'APPROVED';
        }
      })
      .addCase(shareStartup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchEntrepreneurForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEntrepreneurForm.fulfilled, (state, action) => {
        state.loading = false;
        state.entrepreneurForm = action.payload;
      })
      .addCase(fetchEntrepreneurForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateEntrepreneurForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEntrepreneurForm.fulfilled, (state, action) => {
        state.loading = false;
        state.entrepreneurForm = action.payload;
      })
      .addCase(updateEntrepreneurForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateUserPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWaitingComments.pending, (state) => {
        state.loadingComments = true;
        state.errorComments = null;
    })
    .addCase(fetchWaitingUpdates.pending, (state) => {
        state.loadingUpdates = true;
        state.errorUpdates = null;
    })
    .addCase(fetchWaitingUpdates.fulfilled, (state, action) => {
        state.waitingUpdates = action.payload;
        state.loadingUpdates = false;
    })
    .addCase(fetchWaitingUpdates.rejected, (state, action) => {
        state.loadingUpdates = false;
        state.errorUpdates = action.payload;
    })
    .addCase(fetchWaitingComments.fulfilled, (state, action) => {
        state.waitingComments = action.payload;
        state.loadingComments = false;
    })
    .addCase(fetchWaitingComments.rejected, (state, action) => {
        state.loadingComments = false;
        state.errorComments = action.payload;
    })
    .addCase(approveQuestion.fulfilled, (state, action) => {
        state.waitingComments = state.waitingComments.filter(
            comment => comment.questionId !== action.payload.questionId
        );
    })
    .addCase(approveAnswer.fulfilled, (state, action) => {
        state.waitingComments = state.waitingComments.filter(
            comment => comment.answerId !== action.payload.answerId
        );
    })
    .addCase(deleteQuestion.fulfilled, (state, action) => {
        state.waitingComments = state.waitingComments.filter(
            comment => comment.questionId !== action.payload.questionId
        );
    })
    .addCase(deleteAnswer.fulfilled, (state, action) => {
        state.waitingComments = state.waitingComments.filter(
            comment => comment.answerId !== action.payload.answerId
        );
    })
    .addCase(activeShowing.pending, (state)  => {
        state.loading = true;
        state.error = null;
    })
    .addCase(activeShowing.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup) {
            state.selectedStartup.showing = true;
        }
    })
    .addCase(activeShowing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
    })
    .addCase(deactiveShowing.pending, (state)  => {
        state.loading = true;
        state.error = null;
    })
    .addCase(deactiveShowing.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup) {
            state.selectedStartup.showing = false;
        }
    })
    .addCase(deactiveShowing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
    })
    .addCase(activeDetail.pending, (state)  => {
        state.loading = true;
        state.error = null;
    })
    .addCase(activeDetail.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup) {
            state.selectedStartup.detail = true;
        }
    })
    .addCase(activeDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
    })
    .addCase(deactiveDetail.pending, (state)  => {
        state.loading = true;
        state.error = null;
    })
    .addCase(deactiveDetail.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedStartup) {
            state.selectedStartup.detail = false;
        }
    })
    .addCase(deactiveDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
    })
    .addCase(loginAsUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(loginAsUser.fulfilled, (state, action) => {
      state.loading = false;
      // Handle successful login as user if needed
    })
    .addCase(loginAsUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { 
  fetchStatisticsStart, 
  fetchStatisticsSuccess, 
  fetchStatisticsFailure,
  fetchServicesStart,
  fetchServicesSuccess,
  fetchServicesFailure,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  fetchUserDetailStart,
  fetchUserDetailSuccess,
  fetchUserDetailFailure,
  clearSelectedUser,
  blockUserStart,
  blockUserSuccess,
  blockUserFailure,
  unblockUserStart,
  unblockUserSuccess,
  unblockUserFailure,
  fetchUserStartupsStart,
  fetchUserStartupsSuccess,
  fetchUserStartupsFailure,
  fetchStartupDetailStart,
  fetchStartupDetailSuccess,
  fetchStartupDetailFailure,
  fetchUserActivitiesStart,
  fetchUserActivitiesSuccess,
  fetchUserActivitiesFailure,
  fetchPendingStartupsStart,
  fetchPendingStartupsSuccess,
  fetchPendingStartupsFailure,
  changeStartupStatusStart,
  changeStartupStatusSuccess,
  changeStartupStatusFailure,
  fetchCommitteesStart,
  fetchCommitteesSuccess,
  fetchCommitteesFailure,
  fetchPendingCommitteeStart,
  fetchPendingCommitteeSuccess,
  fetchPendingCommitteeFailure,
  fetchAllStartupsStart,
  fetchAllStartupsSuccess,
  fetchAllStartupsFailure,

} = adminSlice.actions;

export const fetchAdminStatistics = () => async (dispatch) => {
  try {
    dispatch(fetchStatisticsStart());
    const response = await ApiHelper.get('/admin/overview');
    dispatch(fetchStatisticsSuccess(response.data));
  } catch (error) {
    dispatch(fetchStatisticsFailure(error.message));
  }
};

export const fetchServiceHealth = () => async (dispatch) => {
  try {
    dispatch(fetchServicesStart());
    const response = await ApiHelper.get('/admin/check-services');
    dispatch(fetchServicesSuccess({
      mkk: response.data.mkk,
      backend: response.data.backend,
      mkkSyncer: response.data.mkkSyncer
    }));
  } catch (error) {
    dispatch(fetchServicesFailure(error.message));
  }
};

export const fetchUsers = (page, limit) => async (dispatch) => {
  try {
    dispatch(fetchUsersStart());
    const response = await ApiHelper.get(`/admin/all-users?page=${page}&limit=${limit}`);
    console.log('Users API Response:', response);
    
    if (response && response.data.response && response.data.response.userList) {
      dispatch(fetchUsersSuccess(response.data.response));
    } else {
      console.error('Invalid users response format:', response.data);
      dispatch(fetchUsersFailure("Invalid response format"));
    }
  } catch (error) {
    console.error('Error fetching users:', error);
    dispatch(fetchUsersFailure(error.message));
  }
};

export const searchUsers = (searchText) => async (dispatch) => {
  try {
    dispatch(fetchUsersStart());
    const response = await ApiHelper.get(`/admin/search-user?text=${encodeURIComponent(searchText)}`);
    console.log('Search Users API Response:', response);
    
    if (response && response.data.response && response.data.response.userList) {
      dispatch(fetchUsersSuccess(response.data.response));
    } else {
      console.error('Invalid search response format:', response.data);
      dispatch(fetchUsersFailure("Invalid response format"));
    }
  } catch (error) {
    console.error('Error searching users:', error);
    dispatch(fetchUsersFailure(error.message));
  }
};

export const fetchUserDetail = (userId) => async (dispatch) => {
  try {
    dispatch(fetchUserDetailStart());
    const response = await ApiHelper.get(`/admin/user/${userId}`);
    console.log('User Detail API Response:', response);
    
    if (response && response.data.userResponse) {
      dispatch(fetchUserDetailSuccess(response.data.userResponse));
    } else {
      console.error('Invalid user detail response format:', response.data);
      dispatch(fetchUserDetailFailure("Invalid response format"));
    }
  } catch (error) {
    console.error('Error fetching user detail:', error);
    dispatch(fetchUserDetailFailure(error.message));
  }
};

export const blockUser = (userId) => async (dispatch) => {
  try {
    dispatch(blockUserStart());
    await ApiHelper.post('/admin/block-user', { userId });
    dispatch(blockUserSuccess());
  } catch (error) {
    console.error('Error blocking user:', error);
    dispatch(blockUserFailure(error.message));
  }
};

export const unblockUser = (userId) => async (dispatch) => {
  try {
    dispatch(unblockUserStart());
    await ApiHelper.post('/admin/unblock-user', { userId });
    dispatch(unblockUserSuccess());
  } catch (error) {
    console.error('Error unblocking user:', error);
    dispatch(unblockUserFailure(error.message));
  }
};

export const fetchUserStartups = (userId) => async (dispatch) => {
  try {
    dispatch(fetchUserStartupsStart());
    const response = await ApiHelper.get(`/admin/startups/${userId}`);
    console.log('User Startups API Response:', response);
    
    if (response?.data?.startupListResponse) {
        console.log(response.data.startupListResponse)
      dispatch(fetchUserStartupsSuccess(response.data.startupListResponse));
    } else {
      dispatch(fetchUserStartupsFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching user startups:', error);
    dispatch(fetchUserStartupsFailure(error.message));
  }
};

export const fetchStartupDetail = (startupId) => async (dispatch) => {
  try {
    dispatch(fetchStartupDetailStart());
    const response = await ApiHelper.get(`/admin/startup/${startupId}`);
    console.log('Startup Detail API Response:', response);
    
    if (response?.data?.startupResponse) {
      const startupData = response.data.startupResponse;
      console.log('Startup Data:', startupData);
      console.log('Startup Status:', startupData.status || startupData.startupStatus);
      
      dispatch(fetchStartupDetailSuccess({
        ...startupData,
        status: startupData.status || startupData.startupStatus || 'PENDING'
      }));
    } else {
      console.error('Invalid startup detail response format:', response.data);
      dispatch(fetchStartupDetailFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching startup detail:', error);
    dispatch(fetchStartupDetailFailure(error.message));
  }
};

export const fetchUserActivities = (userId, page, limit) => async (dispatch) => {
  try {
    dispatch(fetchUserActivitiesStart());
    const response = await ApiHelper.get(`/admin/user-activities/${userId}?page=${page}&limit=${limit}`);
    console.log('User Activities API Response:', response);
    
    if (response?.data?.activities) {
      dispatch(fetchUserActivitiesSuccess(response.data.activities));
    } else {
      console.error('Invalid user activities response format:', response.data);
      dispatch(fetchUserActivitiesFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching user activities:', error);
    dispatch(fetchUserActivitiesFailure(error.message));
  }
};

export const fetchPendingStartups = () => async (dispatch, getState) => {
  try {
    dispatch(fetchPendingStartupsStart());
    const { adminInfo } = getState().auth;
    console.log("Admin Info", adminInfo)
    if (adminInfo?.type?.includes('SUPER_ADMIN')) {
      const response = await ApiHelper.get('/admin/pending-startups');
      console.log("API Response", response)
      if (response.data.code === '00000' && response.data.pendingStartupsResponse) {
        console.log("API Response", response)
        dispatch(fetchPendingStartupsSuccess({
          list: response.data.pendingStartupsResponse,
          count: response.data.pendingStartupsResponse.length
        }));
      } else {
        dispatch(fetchPendingStartupsFailure('Failed to fetch pending startups'));
      }
    } else {
      const response = await ApiHelper.get('/admin/pending');
      if (response?.data?.startups) {
        dispatch(fetchPendingStartupsSuccess({
          list: response.data.startups,
          count: response.data.count || response.data.startups.length
        }));
      } else {
        dispatch(fetchPendingStartupsFailure('Invalid response format'));
      }
    }
  } catch (error) {
    dispatch(fetchPendingStartupsFailure(error.message));
  }
};

export const changeStartupStatus = (startupId, status, reason) => async (dispatch) => {
  try {
    dispatch(changeStartupStatusStart());
    console.log('Sending status change request:', { startupId, status });
    
    let response = null;

    if (status === 'REJECTED') {
      response = await ApiHelper.post('/admin/change-startup-status', { startupId, status, rejectedReason: reason });
    } else {
      response = await ApiHelper.post('/admin/change-startup-status', { startupId, status });
    }

    console.log('Status change response:', response);
    
    if (response?.data?.success) {
      dispatch(changeStartupStatusSuccess(status));
      
      const detailResponse = await ApiHelper.get(`/admin/startup/${startupId}`);
      console.log('Updated startup detail:', detailResponse);
      
      if (detailResponse?.data?.startupResponse) {
        await dispatch(fetchStartupDetailSuccess(detailResponse.data.startupResponse));
      }
      
      await dispatch(fetchPendingStartups());
    } else {
      dispatch(changeStartupStatusFailure('Status update failed'));
    }
  } catch (error) {
    console.error('Error changing startup status:', error);
    dispatch(changeStartupStatusFailure(error.message));
  }
};

export const fetchCommittees = (startupId) => async (dispatch) => {
  try {
    dispatch(fetchCommitteesStart());
    const response = await ApiHelper.get(`/admin/committee/${startupId}`);
    console.log('Committees API Response:', response);
    
    if (response?.data?.committees) {
      dispatch(fetchCommitteesSuccess(response.data.committees));
    } else {
      dispatch(fetchCommitteesFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching committees:', error);
    dispatch(fetchCommitteesFailure(error.message));
  }
};

export const fetchPendingCommittee = () => async (dispatch) => {
  try {
    dispatch(fetchPendingCommitteeStart());
    const response = await ApiHelper.get('/admin/pending-committee');
    console.log('Pending Committee API Response:', response);
    
    if (response?.data?.code === '00000') {
      // Transform the committees array into the expected format
      const transformedData = {
        list: response.data.committees || [],
        count: response.data.committees?.length || 0
      };
      
      dispatch(fetchPendingCommitteeSuccess(transformedData));
    } else {
      dispatch(fetchPendingCommitteeFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching pending committee startups:', error);
    dispatch(fetchPendingCommitteeFailure(error.message));
  }
};

export const fetchAllStartups = (filters) => async (dispatch) => {
  try {
    dispatch(fetchAllStartupsStart());
    
    const queryParams = new URLSearchParams();
    
    // Add pagination params
    queryParams.append('page', filters.page || 1);
    queryParams.append('limit', filters.limit || 10);
    
    // Add filter params if they exist
    if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
    if (filters.status && filters.status.length > 0) queryParams.append('status', filters.status.join(','));

    const response = await ApiHelper.get(`/admin/all-startups?${queryParams.toString()}`);
    console.log('All Startups API Response:', response);

    if (response?.data?.code === '00000' && response?.data?.startupListResponse) {
      dispatch(fetchAllStartupsSuccess({
        list: response.data.startupListResponse.startups || [],
        count: response.data.startupListResponse.count || 0
      }));
    } else {
      console.error('Invalid response format:', response);
      dispatch(fetchAllStartupsFailure('Invalid response format'));
    }
  } catch (error) {
    console.error('Error fetching all startups:', error);
    dispatch(fetchAllStartupsFailure(error.message));
  }
};

export default adminSlice.reducer;